import React, { useEffect, useState } from 'react';

import { api, setAuthToken, getAuthToken } from './services/api';

import BrandLogo from './components/BrandLogo';

import BodegaSidebarHistorial from './components/BodegaSidebarHistorial';

import BodegaInbox from './pages/BodegaInbox';

import BodegaReview from './pages/BodegaReview';

import FacturacionReview from './pages/FacturacionReview';

import { APP_NOMBRE, APP_PIN_SUB, APP_PORTAL, IS_FACTURACION } from './config';

import { useNewOrderAlert } from './hooks/useNewOrderAlert';

import useOfflineBodegaSync from './hooks/useOfflineBodegaSync';

import useRealtimeSync from './hooks/useRealtimeSync';

import { unlockNewOrderSound, playNewOrderSound, isOrderSoundUnlocked, onSoundUnlockChange } from './utils/newOrderSound';



const USER_KEY = 'oh_bodega_user';

const SIDEBAR_KEY = 'oh_bodega_sidebar_collapsed';

const ReviewPage = IS_FACTURACION ? FacturacionReview : BodegaReview;



function loadUser() {

  try {

    const raw = sessionStorage.getItem(USER_KEY);

    return raw ? JSON.parse(raw) : null;

  } catch {

    return null;

  }

}



function LoginGate({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backendOk, setBackendOk] = useState(null);

  function checkBackend() {
    return api
      .ping()
      .then(() => {
        setBackendOk(true);
        return true;
      })
      .catch(() => {
        setBackendOk(false);
        return false;
      });
  }

  useEffect(() => {
    checkBackend();
    const t = setInterval(checkBackend, 45000);
    const onVis = () => {
      if (document.visibilityState === 'visible') checkBackend();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    await unlockNewOrderSound();
    setError('');
    setLoading(true);
    try {
      // Reintenta ping; si el API volvió, quitar el aviso antes de entrar.
      await checkBackend();
      const res = await api.loginPin(pin);
      sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));
      await playNewOrderSound({ title: 'Sonido OK', short: true });
      onUnlock({ token: res.data.token, usuario: res.data?.usuario });
    } catch (err) {
      setError(err.message);
      checkBackend();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pin-screen">
      <div className="pin-screen-glow" aria-hidden="true" />
      <div className="pin-card">
        <BrandLogo variant="pin" />
        <p className="pin-kicker">¡Oh! Belleza</p>
        <h1>{APP_NOMBRE}</h1>
        <p className="pin-sub">{APP_PIN_SUB}</p>
        <span className="pin-role">{APP_NOMBRE}</span>
        {backendOk === false ? (
          <p className="error backend-warn">
            Sin conexión al servidor. En esta tablet: 1) Fecha y hora automática ON 2) Wi‑Fi →
            DNS 8.8.8.8 y 8.8.4.4 (si no puedes abrir DNS privado, ignóralo) 3) Prueba hotspot del
            celular. Luego Reintentar.
            <button type="button" className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => checkBackend()}>
              Reintentar
            </button>
          </p>
        ) : null}
        <form className="pin-form" onSubmit={submit}>
          <label className="pin-label" htmlFor="bodega-pin">
            PIN
          </label>
          <div className="pin-input-wrap">
            <input
              id="bodega-pin"
              className="pin-input-text"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="••••"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              autoFocus
            />
          </div>
          {error ? <p className="error">{error}</p> : null}
          <button type="submit" className="btn-primary btn-lg" disabled={loading || pin.length < 4}>
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function App() {

  const [session, setSession] = useState(() => {

    const savedToken = getAuthToken();

    if (!savedToken) return null;

    setAuthToken(savedToken);

    return { token: savedToken, usuario: loadUser() };

  });

  const [selectedId, setSelectedId] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const [toast, setToast] = useState('');

  const [checkingSession, setCheckingSession] = useState(Boolean(getAuthToken()));

  const [soundOn, setSoundOn] = useState(() => isOrderSoundUnlocked());

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {

    try {

      return localStorage.getItem(SIDEBAR_KEY) === '1';

    } catch {

      return false;

    }

  });



  function toggleSidebar() {

    setSidebarCollapsed((prev) => {

      const next = !prev;

      try {

        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');

      } catch {

        /* ignore */

      }

      return next;

    });

  }

  /* Compactar menú en tablets chicas / horizontal bajo para que el pedido quepa */
  useEffect(() => {
    document.title = IS_FACTURACION ? '¡Oh! Belleza — Facturación' : '¡Oh! Belleza — Bodega';
    const mq = window.matchMedia(
      '(max-width: 1024px), ((orientation: landscape) and (max-height: 600px))'
    );
    const apply = () => {
      if (!mq.matches) return;
      setSidebarCollapsed(true);
    };
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);



  useEffect(() => {

    const saved = getAuthToken();

    if (!saved) {

      setCheckingSession(false);

      return undefined;

    }

    setAuthToken(saved);

    api

      .me()

      .then((res) => {

        sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));

        setSession({ token: saved, usuario: res.data?.usuario });

      })

      .catch(() => {

        api.logout().catch(() => {});

        sessionStorage.removeItem(USER_KEY);

        setSession(null);

      })

      .finally(() => setCheckingSession(false));

    return undefined;

  }, []);



  useEffect(() => {

    if (!toast) return undefined;

    const t = setTimeout(() => setToast(''), 4500);

    return () => clearTimeout(t);

  }, [toast]);



  const token = session?.token || '';



  useEffect(() => onSoundUnlockChange(setSoundOn), []);



  useEffect(() => {

    if (!token) return undefined;

    const keepAlive = () => {

      unlockNewOrderSound();

    };

    window.addEventListener('pointerdown', keepAlive);

    window.addEventListener('touchstart', keepAlive, { passive: true });

    window.addEventListener('keydown', keepAlive);

    const t = setInterval(() => {

      if (document.visibilityState === 'visible') unlockNewOrderSound();

    }, 20000);

    return () => {

      clearInterval(t);

      window.removeEventListener('pointerdown', keepAlive);

      window.removeEventListener('touchstart', keepAlive);

      window.removeEventListener('keydown', keepAlive);

    };

  }, [token]);



  const getToken = React.useCallback(() => getAuthToken(), []);

  const onRealtimeInvalidate = React.useCallback((domains) => {

    if (domains.includes('order') || domains.includes('user')) {

      setRefreshKey((k) => k + 1);

    }

  }, []);

  const onRealtimeReconnect = React.useCallback(() => {

    setRefreshKey((k) => k + 1);

  }, []);

  useRealtimeSync({

    app: IS_FACTURACION ? 'facturacion' : 'bodega',

    enabled: Boolean(token) && !checkingSession,

    getToken,

    onInvalidate: onRealtimeInvalidate,

    onReconnect: onRealtimeReconnect,

  });



  useNewOrderAlert(token, {

    enabled: Boolean(token) && !checkingSession,

    refreshKey,

    onNewOrder: (pedido, n) => {

      const num = pedido?.numero || 'nuevo';

      setToast(

        n > 1

          ? IS_FACTURACION
            ? `${n} pedidos listos para facturar`
            : `${n} pedidos aprobados llegaron a bodega`

          : IS_FACTURACION
            ? `Pedido ${num} listo para facturar`
            : `Pedido ${num} aprobado — por preparar`

      );

      setRefreshKey((k) => k + 1);

    },

  });



  const offlineSync = useOfflineBodegaSync({

    enabled: Boolean(token) && !checkingSession && !IS_FACTURACION,

  });



  async function activarSonido() {

    await unlockNewOrderSound();

    await playNewOrderSound({ title: 'Sonido OK', short: true });

    setSoundOn(isOrderSoundUnlocked());

    setToast(isOrderSoundUnlocked() ? 'Sonido de pedidos activo' : 'Toca de nuevo para activar el sonido');

  }



  function logout() {

    api.logout().catch(() => {});

    sessionStorage.removeItem(USER_KEY);

    setSession(null);

    setSelectedId(null);

  }



  if (checkingSession) {

    return (

      <div className="pin-screen">

        <p className="muted">Verificando sesión...</p>

      </div>

    );

  }



  if (!session?.token) {

    return <LoginGate onUnlock={setSession} />;

  }



  return (

    <div className={`pedidos-app bodega-app${sidebarCollapsed ? ' sidebar-is-collapsed' : ''}`}>

      <aside className={`pedidos-sidebar bodega-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>

        {!sidebarCollapsed ? (

          <>

            <BrandLogo variant="sidebar" />

            <p className="sidebar-tag">{APP_PORTAL}</p>

            {session?.token ? (
              <p className="sidebar-user">Sesión activa</p>
            ) : null}

            <BodegaSidebarHistorial

              pin={token}

              selectedId={selectedId}

              onSelect={setSelectedId}

              refreshKey={refreshKey}

            />

          </>

        ) : (

          <div className="sidebar-mini-brand" title={session.usuario?.nombre || 'Bodega'}>

            {(session.usuario?.nombre || 'B').charAt(0).toUpperCase()}

          </div>

        )}

        <button

          type="button"

          className="sidebar-collapse-btn"

          onClick={toggleSidebar}

          title={sidebarCollapsed ? 'Expandir panel' : 'Minimizar panel'}

          aria-label={sidebarCollapsed ? 'Expandir panel' : 'Minimizar panel'}

        >

          {sidebarCollapsed ? '»' : '«'}

        </button>

        <button type="button" className="logout" onClick={logout} title="Cerrar sesión">

          {sidebarCollapsed ? 'Salir' : 'Cerrar sesión'}

        </button>

      </aside>

      <main className="pedidos-main">

        {!soundOn ? (

          <div className="sound-unlock-banner">

            <span>Activa el sonido para oír cuando llegue un pedido</span>

            <button type="button" className="btn-sound-unlock" onClick={activarSonido}>

              Activar sonido

            </button>

          </div>

        ) : (

          <div className="sound-ok-bar">

            <span>Sonido de pedidos activo</span>

            <button type="button" className="link-btn" onClick={activarSonido}>

              Probar

            </button>

          </div>

        )}

        {offlineSync.pending > 0 ? (

          <div className="offline-bodega-banner">

            <span>

              {offlineSync.pending} cambio{offlineSync.pending === 1 ? '' : 's'} guardado

              {offlineSync.pending === 1 ? '' : 's'} en la tablet (sin internet)

            </span>

            <button

              type="button"

              className="btn-ghost"

              disabled={offlineSync.syncing}

              onClick={() => offlineSync.syncNow()}

            >

              {offlineSync.syncing ? 'Enviando…' : 'Enviar ahora'}

            </button>

          </div>

        ) : null}

        <div className="pedidos-layout">

          <section className="pedidos-list-pane">

            <BodegaInbox

              pin={token}

              selectedId={selectedId}

              onSelect={setSelectedId}

              refreshKey={refreshKey}

            />

          </section>

          <section className="pedidos-detail-pane">

            {selectedId ? (

              <ReviewPage

                id={selectedId}

                pin={token}

                usuario={session.usuario}

                onBack={() => setSelectedId(null)}

                onUpdated={() => {

                  setRefreshKey((k) => k + 1);

                  setToast('Pedido actualizado');

                }}

              />

            ) : (

              <div className="detail-placeholder">

                <h3>Selecciona un pedido</h3>

                <p className="muted">
                  {IS_FACTURACION
                    ? 'Revisa y factura los pedidos completados en bodega.'
                    : 'Separa productos y marca el pedido como listo.'}
                </p>

              </div>

            )}

          </section>

        </div>

      </main>

      {toast ? <div className="toast">{toast}</div> : null}

    </div>

  );

}


